import isLength from '../utils/validator/isLength';

/*
 * Clase que ejecuta las diferentes validaciones que pueda tener algún campo en un formulario
 * Si no se cumple con alguno de los métodos de validación, se agrega a currentError una propiedad con
 * el mensaje de error correspondiente.
 * */
export default class {
  constructor(fieldValue = '', fieldError) {
    this.fieldValue = fieldValue;
    this.fieldError = fieldError;
    this.currentError = {};
  }

  /*
   * Valida que la longitud del valor del campo sea mayor o igual a minLength.
   * @param {number} minLength longitud mínima del valor del campo
   * @param {string} errorMessage texto que se mostrará en el mensaje de error
   * */
  validateMinLongitude(minLength, errorMessage) {
    if (this.fieldValue.length < minLength) {
      this.setError(errorMessage);
    }
    return this;
  }

  /*
   * Valida que la longitud del valor del campo sea menor o igual a maxLength.
   * @param {number} maxLength longitud máxima del valor del campo
   * @param {string} errorMessage texto que se mostrará en el mensaje de error
   * */
  validateMaxLongitude(maxLength, errorMessage) {
    if (this.fieldValue.length > maxLength) {
      this.setError(errorMessage);
    }
    return this;
  }

  /*
   * Valida que el correo electrónico ingresado sea valido.
   * @param {string} errorMessage texto que se mostrará en el mensaje de error
   * */
  validateEmailFormat(errorMessage) {
    const emailPattern = /^(([^<>()\[\]\\:;@"]+(\.[^<>()\[\]\\:;@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!this.isValidPattern(emailPattern, this.fieldValue)) {
      this.setError(errorMessage);
    }
    return this;
  }

  /*
   * Valida que el correo electrónico ingresado sea valido.
   * @param {string} errorMessage texto que se mostrará en el mensaje de error
   * */
  validateEmailFormatTelmex(errorMessage) {
    const emailPattern = /^(([^<>()\[\]\\:;@"]+(\.[^<>()\[\]\\:;@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!this.isValidPattern(emailPattern, this.fieldValue)) {
      this.setError(errorMessage);
    }
    return this;
  }

  /*
   * Valida que el nombre(s) o apellido(s) ingresado sea tenga sólo caracteres, letras con acentos, dieresis, Ñ y espacios.
   * @param {string} errorMessage texto que se mostrará en el mensaje de error
   * */
  validateNameFormat(errorMessage) {
    const namePattern = /^[a-zA-Z0-9\s.,´'\-_+&\u00C0-\u00ff]+$/;
    if (!this.isValidPattern(namePattern, this.fieldValue)) {
      this.setError(errorMessage);
    }
    return this;
  }

  /*
   * Valida que los campos numéricos sólo tengan los caracteres permitidos.
   * @param {string} errorMessage texto que se mostrará en el mensaje de error
   * */
  validateNumberFormat(errorMessage) {
    function isPhone(phone) {
      return phone == 'telefono';
    }

    const numberPattern = /^[0-9]+$/;

    if (isPhone(this.fieldError)) {
      if (!this.isValidPatternTel(numberPattern, this.fieldValue)) {
        this.setError(errorMessage);
      }
    } else if (!this.isValidPattern(numberPattern, this.fieldValue)) {
      this.setError(errorMessage);
    }

    return this;
  }

  /*
   * Valida que los valores del campo actual y su campo de confirmación coincidan
   * @param {string} confirmationValue texto ingresado en el campo de confirmación
   * @param {string} errorMessage texto que se mostrará en el mensaje de error
   * */
  validateConfirmationValue(confirmationValue, errorMessage) {
    // Si el campo de confirmación no tiene ningún valor, no se setea ningún error
    if (confirmationValue === '') return this;
    if (confirmationValue !== this.fieldValue) {
      this.setError(errorMessage);
    }
    return this;
  }

  /*
   * Valida que el checkbox esté marcado
   * @param {string} errorMessage texto que se mostrará en el mensaje de error
   * */
  isChecked(errorMessage) {
    if (this.fieldValue === 0) {
      this.setError(errorMessage);
    }
    return this;
  }

  /*
   * Valida que el campo evaluado sea diferente a un valor default. Si son iguales se setea un mensaje de error
   * @param {number | string} valor default contra el que se compara el campo
   * @param {string} errorMessage texto que se mostrará en el mensaje de error
   * */
  isEqualTo(value, errorMessage) {
    if (this.fieldValue === value) {
      this.setError(errorMessage);
    }
    return this;
  }

  /*
   * Valida que el campo evaluado sea diferente a un valor default. Si son iguales se setea un mensaje de error
   * @param {number | string} valor default contra el que se compara el campo
   * @param {string} errorMessage texto que se mostrará en el mensaje de error
   * */
  isEqual(value, errorMessage) {
    if (this.fieldValue !== value) {
      this.setError(errorMessage);
    }
    return this;
  }

  isValidPin(errorMessage) {
    if (!isLength(this.fieldValue, { min: 4, max: 6 })) {
      this.setError(errorMessage);
    }
    return this;
  }

  /*
   * Regresa el objeto currentError que almacena todos los errores que se registraron
   * durante la validación del campo
   * */
  getErrors() {
    return this.currentError;
  }

  /*
   * Setea el mensaje de error que se envia como parametro en el objeto que almacena todos los errores
   * @param {string} errorMessage texto que se mostrará en el mensaje de error
   * */
  setError(errorMessage) {
    // Sólo se almacena el primer mensaje de error que ocurra.
    if (this.currentError[this.fieldError] === undefined) {
      this.currentError[this.fieldError] = errorMessage;
    }
    return this;
  }

  /*
   * Regresa un valor boolean resultado de la evaluación del string value contra el regex
   * @param {regex} regex patron que debe cumplir el texto ingresado
   * @param {string} value string que contiene el texto ingresado por el usuario
   * */
  isValidPattern(regex, value) {
    return regex.test(value);
  }

  isValidPatternTel(regex, value) {
    if (parseInt(value.length, 10) === 10) return regex.test(value);
    return false;
  }
}
